import {
  Box,
  Grid2 as Grid,
  Paper,
  Typography,
  Avatar,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import { AdminBusinessDetail } from '../../../types/admin';
import { formatDistanceToNow } from 'date-fns';

interface BusinessOverviewTabProps {
  business: AdminBusinessDetail;
}

/**
 * BusinessOverviewTab - Business overview with key metrics and info
 *
 * Features:
 * - Business information card
 * - Owner information with avatar
 * - Key metrics (agents, clients, revenue)
 * - Status and dates
 * - Quick stats cards
 */
export default function BusinessOverviewTab({ business }: BusinessOverviewTabProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSizeBadgeColor = (size: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (size) {
      case 'small':
        return 'default';
      case 'medium':
        return 'primary';
      case 'large':
        return 'success';
      default:
        return 'default';
    }
  };

  const getSizeLabel = (size: string): string => {
    switch (size) {
      case 'small':
        return 'Small (1-3 agents)';
      case 'medium':
        return 'Medium (4-10 agents)';
      case 'large':
        return 'Large (11+ agents)';
      default:
        return size;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Business Information Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <BusinessIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {business.name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Chip
                label={business.status === 'active' ? 'Active' : 'Inactive'}
                size="small"
                color={business.status === 'active' ? 'success' : 'default'}
              />
              <Chip
                label={getSizeLabel(business.size_category)}
                size="small"
                color={getSizeBadgeColor(business.size_category)}
                variant="outlined"
              />
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Business Details Grid */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(business.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}{' '}
                  ({formatDistanceToNow(new Date(business.created_at), { addSuffix: true })})
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UpdateIcon fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Activity
                </Typography>
                <Typography variant="body2">
                  {formatDistanceToNow(new Date(business.last_activity), { addSuffix: true })}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Owner Information Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Business Owner
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={business.owner.picture}
            alt={business.owner.name}
            sx={{ width: 56, height: 56 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1" fontWeight={500}>
              {business.owner.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography
                variant="body2"
                color="text.secondary"
                component="a"
                href={`mailto:${business.owner.email}`}
                sx={{
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline', color: 'primary.main' },
                }}
              >
                {business.owner.email}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Member since{' '}
              {new Date(business.owner.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
              })}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Key Metrics Cards */}
      <Grid container spacing={2}>
        {/* Agents */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              textAlign: 'center',
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {business.agents_count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Agents
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {business.analytics.agents.active} active
            </Typography>
          </Paper>
        </Grid>

        {/* Clients */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              textAlign: 'center',
              bgcolor: 'success.50',
              border: '1px solid',
              borderColor: 'success.100',
            }}
          >
            <GroupsIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="success.main">
              {business.clients_count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Clients
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {business.analytics.clients.active} active
            </Typography>
          </Paper>
        </Grid>

        {/* Total Revenue */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              textAlign: 'center',
              bgcolor: 'warning.50',
              border: '1px solid',
              borderColor: 'warning.100',
            }}
          >
            <AttachMoneyIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="warning.main">
              {formatCurrency(business.total_revenue).replace('$', '')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {formatCurrency(business.monthly_revenue)}/month
            </Typography>
          </Paper>
        </Grid>

        {/* Revenue Trend */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              textAlign: 'center',
              bgcolor: business.analytics.revenue.trend >= 0 ? 'success.50' : 'error.50',
              border: '1px solid',
              borderColor: business.analytics.revenue.trend >= 0 ? 'success.100' : 'error.100',
            }}
          >
            {business.analytics.revenue.trend >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            )}
            <Typography
              variant="h4"
              fontWeight={700}
              color={business.analytics.revenue.trend >= 0 ? 'success.main' : 'error.main'}
            >
              {business.analytics.revenue.trend > 0 ? '+' : ''}
              {business.analytics.revenue.trend.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revenue Trend
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Last 6 months
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Metrics */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Performance Metrics
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Avg Clients per Agent
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.agents.avg_clients_per_agent}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Conversion Rate
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {(business.analytics.performance.conversion_rate * 100).toFixed(1)}%
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Avg Deal Size
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatCurrency(business.analytics.performance.avg_deal_size)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Client Churn Rate
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.clients.churn_rate.toFixed(1)}%
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              New Clients This Month
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.clients.new_this_month}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Client Satisfaction
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {business.analytics.performance.client_satisfaction.toFixed(1)}/5.0
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
